import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { TextInput, Icon, ButtonV2, Preloader, TagItem, SelectList } from 'components';
import { getErrorText } from 'lib/utils';
import './style.scss';

class SelectTagsList extends Component {
  static propTypes = {
    tags: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string
      })
    ),

    selectedTagIds: PropTypes.arrayOf(PropTypes.string),

    onChange: PropTypes.func,

    onCreate: PropTypes.func // Must return a promise
  };

  static defaultProps = {
    tags: [],
    selectedTagIds: []
  };

  static filterByTerm(items = [], propName, term) {
    if (!term) {
      return items;
    }

    term = term.toLowerCase().trim();

    return items.filter(
      i =>
        i[propName]
          .toLowerCase()
          .trim()
          .indexOf(term) >= 0
    );
  }

  static buildTagMap(tags) {
    const map = {};

    tags.forEach(t => {
      map[t.id] = t;
    });

    return map;
  }

  constructor(props) {
    super(props);

    this._tagsMap = SelectTagsList.buildTagMap(props.tags);
    this._filteredTags = [];

    this.state = {
      search: '',
      selectedItemIndex: 0,
      loading: false
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.tags !== this.props.tags) {
      this._tagsMap = SelectTagsList.buildTagMap(this.props.tags);
    }

    if (this._filteredTags.length === 0 && this.state.selectedItemIndex !== -1) {
      this.setState({ selectedItemIndex: -1 });
    }
  }

  @bind
  changeSearch(text) {
    this.setState({ search: text });
  }

  @bind
  addTag(tag) {
    if (this.props.selectedTagIds.indexOf(tag.id) !== -1) {
      return;
    }

    this.setState({ loading: true }, () => {
      this.props.onChange(this.props.selectedTagIds.concat([tag.id]));
      this.setState({ loading: false });
    });
  }

  @bind
  deleteTag(tagId) {
    this.props.onChange(this.props.selectedTagIds.filter(id => id !== tagId));

    if (this.state.selectedItemIndex === -1) {
      this.state.selectedItemIndex = 0;
    }
  }

  @bind
  createTag() {
    this.setState({ loading: true }, async () => {
      try {
        await this.props.onCreate(this.state.search);
        this.setState({ loading: false, search: '' });
      } catch (e) {
        Actions.reportError('Unable to create a tag:\n\n' + getErrorText(e.response.data), e);
        this.setState({ loading: false });
      }
    });
  }

  renderListItem(t) {
    return (
      <div className="select-tags-list__list-item" key={t.id}>
        {t.name}
      </div>
    );
  }

  renderTags() {
    const selectedTagsMap = {};
    this.props.selectedTagIds.forEach(id => {
      selectedTagsMap[id] = true;
    });

    const tagItems = (this._filteredTags = SelectTagsList.filterByTerm(
      this.props.tags,
      'name',
      this.state.search
    ).filter(t => !selectedTagsMap[t.id]));

    if (tagItems.length === 0) {
      return <div className="select-tags-list__no-tags-label">No tags found</div>;
    }

    return <SelectList items={tagItems} renderItem={this.renderListItem} onSelect={this.addTag} />;
  }

  renderSelectedTags() {
    return this.props.selectedTagIds.map(id => <TagItem key={id} tag={this._tagsMap[id]} onDelete={this.deleteTag} />);
  }

  render() {
    const { search, loading } = this.state;
    const { tags, selectedTagIds } = this.props;
    const isSelected = selectedTagIds.length !== 0;
    const isNewTag = tags.findIndex(t => t.name.toLowerCase().trim() === search.toLowerCase().trim()) === -1;

    return (
      <div className="select-tags-list">
        <div className="select-tags-list__header">
          <div className="select-tags-list__search">
            <Icon size={18} name="search" className="select-tags-list__search-icon" />

            <TextInput
              type="text"
              value={search}
              className="select-tags-list__search-input"
              placeholder="Enter a Tag"
              onChange={this.changeSearch}
            />
          </div>
        </div>

        <div className="select-tags-list__inner">
          {isSelected ? (
            <div className="select-tags-list__selected-tags">{this.renderSelectedTags()}</div>
          ) : (
            <div className="select-tags-list__no-tags-label">No tags selected</div>
          )}

          <div className="select-tags-list__list">{this.renderTags()}</div>

          {search.length > 0 && isNewTag && (
            <div className="select-tags-list__new-tag">
              <ButtonV2 label={`Create tag "${search}"`} onClick={this.createTag} disabled={loading} />

              {loading && <Preloader small className="select-tags-list__loading" />}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default SelectTagsList;
